import React, {useEffect} from "react";
import styles from "./LocationTable.module.css";
import {LocationTableHeader} from "./header/LocationTableHeader";
import {LocationTableRow} from "./row/LocationTableRow";
import {LocationTableControls} from "./controls/LocationTableControls";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {
    fetchLocationList,
    locationListRequestStatusSelector,
    locationListSelector,
    locationListTotalCountSelector,
    locationTableRequestSelector,
    LOCATIONS_TABLE_ITEMS_ON_PAGE,
    updateLocationTableRequest
} from "../locationListSlice";
import {Button, ButtonState} from "../../../elements/button/Button";
import {FormName, openForm} from "../../../elements/formProvider/formSlice";
import {RequestStatus} from "../../../../api/apiConfiguration";
import {TablePagination} from "../../../elements/pagination/TablePagination";
import {TableLoader} from "../../../elements/tableLoader/TableLoader";
import {showErrorToastNotification} from "../../../elements/toastNotification/toastNotificationSlice";

interface TableProps {
}

export const LocationTable: React.FC<TableProps> = () => {

    const dispatch = useAppDispatch()
    const locationList = useAppSelector(locationListSelector)
    const locationListTotalCount = useAppSelector(locationListTotalCountSelector)
    const locationListRequestStatus = useAppSelector(locationListRequestStatusSelector)
    const locationTableRequest = useAppSelector(locationTableRequestSelector)

    useEffect(() => {
        dispatch(fetchLocationList())
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationTableRequest]);

    useEffect(() => {
        if (locationList.length === 0 && locationTableRequest.pageNumber > 0 && locationListRequestStatus === RequestStatus.RESOLVED) {
            dispatch(updateLocationTableRequest({pageNumber: locationTableRequest.pageNumber - 1}))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationList.length, locationTableRequest.pageNumber, locationListRequestStatus]);

    const handleClick = () => {
        dispatch(openForm({name: FormName.CREATE_LOCATION}))
    }

    const hasActiveFilters = () => {
        return locationTableRequest.searchQuery ||
               (locationTableRequest.vehicleDataProviders && locationTableRequest.vehicleDataProviders.length > 0) ||
               locationTableRequest.aftersalesEnabled !== undefined;
    }

    if (locationListRequestStatus === RequestStatus.REJECTED) {
        dispatch(showErrorToastNotification({message: "Failed to load locations"}))
    }

    const isLoading = locationListRequestStatus === RequestStatus.PENDING;
    const isResolved = locationListRequestStatus === RequestStatus.RESOLVED;

    return <div className={styles.wrapper}>
        <div className={styles.tableWrapper}>
            <LocationTableControls/>
            <LocationTableHeader/>
            <div className={styles.tableRowsWrapper}>
                {isLoading && <TableLoader/>}

                {isResolved &&
                    locationList.map((location, index) => {
                    return <React.Fragment key={`location-table-row-${index}`}>
                        <LocationTableRow
                            index={index}
                            location={location}
                            isLastRow={index === (locationList.length - 1)}
                        />
                    </React.Fragment>
                })}
            </div>
        </div>
        {isResolved && locationListTotalCount === 0 && !hasActiveFilters() ? (
            <div className={styles.emptyTableWrapper}>
                <div className={styles.titleWrapper}>
                    <span className={styles.title}>You haven’t created any location yet</span>
                    <span
                        className={styles.subtitle}>Get started by creating a location. Once created, it will appear here.</span>
                </div>
                <Button
                    title={"Create location"}
                    iconSrc={"/plusIcon.svg"}
                    state={ButtonState.PRIMARY}
                    handleClick={handleClick}
                />
            </div>
        ) : (
            <TablePagination
                pageNumber={locationTableRequest.pageNumber}
                totalItems={locationListTotalCount}
                itemsPerPage={LOCATIONS_TABLE_ITEMS_ON_PAGE}
                onPageChange={(newPage) => dispatch(updateLocationTableRequest({pageNumber: newPage}))}
            />
        )}
    </div>
}
